<template>
  <!-- Figma 1:13536 › ຮ້ານຄ້າຖອນ COD ລ່ວງໜ້າ (711:528). -->
  <div class="container list-page cod-shops">
    <div class="list-head">
      <h2 class="list-title">
        <i class="fal fa-store"></i>
        {{ $t("codAdvance.menu_shops") }}
        <span class="list-count">({{ pagination.total }})</span>
      </h2>
      <div class="head-actions">
        <!-- Owner, 2026-10-05: enabling, % withdraw and fee changes wait for ຜູ້ບໍລິຫານ. -->
        <button v-if="totalPending" type="button" class="pill is-pending pending-chip" @click="showPending">
          <i class="fal fa-hourglass-half"></i> {{ $t("codAdvance.pending_approval") }} ({{ totalPending }})
        </button>
        <button class="cod-btn is-pale list-head-action" @click="openCreate">{{ $t("codAdvance.add_shop") }}</button>
      </div>
    </div>

    <div class="filter-card">
      <div class="filter-field">
        <label for="cod-shops-search">{{ $t("codAdvanceReport.search") }}:</label>
        <input id="cod-shops-search" class="filter-input" type="text" v-model="filters.search"
               :placeholder="$t('codAdvance.search_placeholder')" @keyup.enter="fetchData(1)">
      </div>
      <div class="filter-field">
        <label for="cod-shops-status">{{ $t("codAdvance.shop_status") }}:</label>
        <select id="cod-shops-status" class="filter-input" v-model="filters.status" @change="fetchData(1)">
          <option value="all">{{ $t("common.all") }}</option>
          <option value="enabled">{{ $t("codAdvance.filter_enabled") }}</option>
          <option value="disabled">{{ $t("codAdvance.filter_disabled") }}</option>
          <option value="pending_approval">{{ $t("codAdvance.pending_approval") }}</option>
        </select>
      </div>
      <button class="round-btn" type="button" :aria-label="$t('codAdvanceReport.search')" @click="fetchData(1)">
        <i class="fal fa-search"></i>
      </button>
    </div>

    <div class="table-card">
      <vs-table :data="rows">
        <template #thead>
          <vs-tr>
            <vs-th>{{ $t("codAdvance.no") }}</vs-th>
            <vs-th>{{ $t("codAdvance.shop_name") }}</vs-th>
            <vs-th>{{ $t("codAdvanceReport.tel") }}</vs-th>
            <vs-th>{{ $t("codAdvance.created_at") }}</vs-th>
            <vs-th>{{ $t("codAdvance.contract_document") }}</vs-th>
            <vs-th>{{ $t("codAdvance.status") }}</vs-th>
            <vs-th>{{ $t("codAdvance.advance_rate_pct") }}</vs-th>
            <vs-th>{{ $t("codAdvance.fee") }}</vs-th>
            <vs-th>{{ $t("codAdvance.withdraw_mode_short") }}</vs-th>
            <vs-th></vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr :key="row.id" v-for="(row, index) in rows">
            <vs-td>{{ rowNumber(index) }}</vs-td>
            <vs-td>{{ row.shop_name }}</vs-td>
            <vs-td>{{ row.customer ? row.customer.tel : '—' }}</vs-td>
            <vs-td>{{ formatDateTime(row.created_at) }}</vs-td>
            <vs-td>
              <a v-if="row.contract_url" class="thumb" :href="row.contract_url" target="_blank"
                 rel="noopener" :title="row.shop_name">
                <img v-if="isImage(row.contract_mime)" :src="row.contract_url" alt="">
                <i v-else class="fal fa-file-pdf"></i>
              </a>
              <span v-else class="text-muted">—</span>
            </vs-td>
            <vs-td>
              <span :class="row.is_enabled ? 'text-ok' : 'text-muted'">{{ row.is_enabled ? $t("codAdvance.on") : $t("codAdvanceReport.close") }}</span>
              <div v-if="row.pending_approval">
                <span class="pill is-pending">{{ $t("codAdvance.pending_approval") }}</span>
              </div>
            </vs-td>
            <!-- The percent and its amount (owner, 2026-10-07): the COD the
                 advance percent applies to, and the fees on what is drawn now (taken / waiting for Finance). -->
            <vs-td>
              {{ formatPct(row.advance_rate_pct) }}
              <!-- Above the boss's ceiling (2026-10-26): what applies is the ceiling. -->
              <span class="cell-note text-busy" v-if="cappedRate(row)">{{ $t("codAdvance.capped_at", { pct: formatPct(row.effective.advance_rate_pct) }) }}</span>
              <span class="cell-note" v-if="row.base_effective > 0">{{ $t("codAdvance.of_base", { amount: formatKip(row.base_effective) }) }}</span>
            </vs-td>
            <vs-td>
              {{ formatPct(row.fee_pct) }}
              <span class="cell-note text-busy" v-if="cappedFee(row)">{{ $t("codAdvance.capped_at", { pct: formatPct(row.effective.fee_pct) }) }}</span>
              <span class="cell-note" v-if="row.fees_taken > 0">{{ $t("codAdvance.fees_taken", { amount: formatKip(row.fees_taken) }) }}</span>
              <!-- On advances waiting for Finance: taken only if the transfer is made. -->
              <span class="cell-note" v-if="row.fees_pending > 0">{{ $t("codAdvance.fees_pending", { amount: formatKip(row.fees_pending) }) }}</span>
            </vs-td>
            <vs-td>{{ withdrawModeLabel(row.withdraw_mode) }}</vs-td>
            <vs-td>
              <div class="row-actions">
                <button v-if="canApprove && row.pending_approval" class="icon-btn is-approve" type="button"
                        :aria-label="$t('codAdvance.review_approval')" :title="$t('codAdvance.review_approval')" @click="openApproval(row)">
                  <i class="fal fa-check"></i>
                </button>
                <button class="round-btn" type="button" :aria-label="$t('codAdvance.edit')" @click="openEdit(row)">
                  <i class="fal fa-pen"></i>
                </button>
              </div>
            </vs-td>
          </vs-tr>
        </template>
        <template #footer>
          <Pagination v-if="pagination.last_page > 1" :pagination="pagination" :offset="10" @paginate="fetchData"/>
        </template>
      </vs-table>

      <p class="list-state is-loading" v-if="isLoading">{{ $t("codAdvance.loading") }}</p>
      <p class="list-state" v-else-if="!rows.length">{{ $t("codAdvance.no_shops") }}</p>
      <p class="list-state is-error" v-if="error">{{ error }}</p>
    </div>

    <vs-dialog prevent-close v-model="modalActive" v-if="modalActive" width="660px">
      <template #header>
        <h4 class="dialog-title">{{ editing && editing.id ? $t("codAdvance.edit_shop") : $t("codAdvance.add_shop") }}</h4>
      </template>
      <ShopFormModal :shop="editing" :can-approve="canApprove" :policy="policy" @close="closeModal" @saved="onSaved"
                     @review="reviewFromForm"/>
    </vs-dialog>

    <vs-dialog prevent-close v-model="approvalActive" v-if="approvalActive" width="500px">
      <template #header>
        <h4 class="dialog-title">{{ $t("codAdvance.review_approval") }}</h4>
      </template>
      <ApprovalModal :approval="approvalTarget.pending_approval" :shop-name="approvalTarget.shop_name"
                     @decided="onDecided"/>
    </vs-dialog>

    <vs-dialog v-model="doneActive" v-if="doneActive" width="370px" not-close>
      <div class="done">
        <i class="fal done-icon" :class="done.waiting ? 'fa-hourglass-half is-waiting' : 'fa-check-circle'"></i>
        <b class="done-title">{{ done.title }}</b>
        <p class="done-text" v-if="done.text">{{ done.text }}</p>
        <button class="cod-btn is-block" type="button" @click="doneActive = false">{{ $t("codAdvance.ok") }}</button>
      </div>
    </vs-dialog>
  </div>
</template>

<script>
import Pagination from '@/Plugins/Paginations/Pagination';
import ShopFormModal from '@/views/Accounting/Customer/CodAdvance/components/ShopFormModal';
import ApprovalModal from '@/views/Accounting/Customer/CodAdvance/components/ApprovalModal';
import {errorMessage, fetchShops, formatDateTime, formatKip, formatPct, withdrawModeLabel} from '@/services/codAdvanceService';

export default {
  name: 'CodAdvanceShops',
  components: {Pagination, ShopFormModal, ApprovalModal},
  data() {
    return {
      rows: [],
      // All shops by default: a shop saved as ປິດ must not vanish from the list
      // the moment it is created.
      filters: {search: '', status: 'all'},
      pagination: {current_page: 1, last_page: 1, per_page: 10, total: 0},
      modalActive: false,
      editing: null,
      // From the list: whether the signed-in user approves (manager / SuperAdmin).
      canApprove: false,
      // The boss's ceilings (2026-10-26): the form keeps a shop inside them.
      policy: null,
      totalPending: 0,
      approvalActive: false,
      approvalTarget: null,
      doneActive: false,
      done: {title: '', text: '', waiting: false},
      isLoading: false,
      error: null,
    };
  },
  created() {
    this.fetchData(1);
  },
  methods: {
    formatDateTime,
    formatKip,
    formatPct,
    withdrawModeLabel,
    rowNumber(index) {
      return (this.pagination.current_page - 1) * this.pagination.per_page + index + 1;
    },
    cappedRate(row) {
      return !!row.effective && Number(row.effective.advance_rate_pct) < Number(row.advance_rate_pct);
    },
    cappedFee(row) {
      return !!row.effective && Number(row.effective.fee_pct) < Number(row.fee_pct);
    },
    isImage(mime) {
      return String(mime || '').indexOf('image/') === 0;
    },
    async fetchData(page) {
      this.isLoading = true;
      this.error = null;
      try {
        const data = await fetchShops({
          search: this.filters.search || undefined,
          status: this.filters.status,
          page: page || this.pagination.current_page,
        });
        this.rows = data.data || [];
        this.canApprove = !!data.can_approve;
        this.policy = data.policy || null;
        this.totalPending = Number(data.total_pending || 0);
        this.pagination = {
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total,
        };
      } catch (e) {
        this.error = errorMessage(e, this.$t('codAdvance.load_failed'));
      } finally {
        this.isLoading = false;
      }
    },
    openCreate() {
      this.editing = null;
      this.modalActive = true;
    },
    openEdit(row) {
      this.editing = row;
      this.modalActive = true;
    },
    closeModal() {
      this.modalActive = false;
      this.editing = null;
    },
    onSaved(saved) {
      this.closeModal();
      this.done = saved && saved.approval_required
        ? {title: this.$t('codAdvance.saved'), text: this.$t('codAdvance.saved_awaiting_approval'), waiting: true}
        : {title: this.$t('codAdvance.save_success'), text: '', waiting: false};
      this.doneActive = true;
      this.fetchData(this.pagination.current_page);
    },
    showPending() {
      this.filters.status = 'pending_approval';
      this.fetchData(1);
    },
    openApproval(row) {
      this.approvalTarget = row;
      this.approvalActive = true;
    },
    reviewFromForm(shop) {
      this.closeModal();
      this.openApproval(shop);
    },
    onDecided(outcome) {
      this.approvalActive = false;
      this.approvalTarget = null;
      this.done = outcome === 'approved'
        ? {title: this.$t('codAdvance.approved_title'), text: this.$t('codAdvance.approved_text'), waiting: false}
        : {title: this.$t('codAdvance.rejected_title'), text: this.$t('codAdvance.rejected_text'), waiting: false};
      this.doneActive = true;
      this.fetchData(this.pagination.current_page);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './components/cod-advance-ui';

.head-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pending-chip {
  border: 0;
  cursor: pointer;
  font-size: 13px;
  padding: 6px 12px;
}

.row-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.icon-btn.is-approve {
  border-color: #cdeedb;
  background: #e9f8ef;
  color: #1e8e4e;
}

.done {
  text-align: center;
  padding: 6px 4px;
}

.done-icon {
  font-size: 44px;
  color: #1e8e4e;

  &.is-waiting {
    color: #b07d00;
  }
}

.done-title {
  display: block;
  font-size: 17px;
  margin: 10px 0 6px;
}

.done-text {
  color: $cod-muted;
  font-size: 13px;
  margin: 0 0 16px;
}

.dialog-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  text-align: center;
  width: 100%;
}
</style>
